import {LobOfficePermission, SubjectRolesAndPermissions, UserContext} from './subject';
import {UserProfileData} from '../../api-client-kt/src';

// Represents a permission row when expressed as a list of LOBs with office permissions
// Example: { lobKey: 'lob1', office1: true, office2: false, office3: true }
export type AppPermissionsByLobRow = { lobKey: string; } & { [officeKey: string]: boolean;}
export type AppPermissionsAsMap = { [lobKey: string]: { [officeKey: string]: boolean } };

export interface AppRolePermissionHeader {
    roles: string[];
    permissions: LobOfficePermission[];
}

export class AppRoleAndPermissions {

    roles: string[];
    permissions: AppPermissionsAsMap = {};

    constructor(roles: string[]) {
        this.roles = roles;
    }

    setPermission(lobKey: string, officeKey: string, value: boolean): void {
        if (!this.permissions[lobKey]) this.permissions[lobKey] = {};
        this.permissions[lobKey][officeKey] = value;
    }

    hasPermission(lobKey: string, officeKey: string): boolean {
        return !!this.permissions[lobKey]?.[officeKey];
    }

    clone(): AppRoleAndPermissions {
        const clone = new AppRoleAndPermissions(this.roles);
        clone.permissions = JSON.parse(JSON.stringify(this.permissions));
        return clone;
    }

    toHeaderValue(): AppRolePermissionHeader {
        const linear: LobOfficePermission[] = [];
        for (const [lobKey, officeKeys] of Object.entries(this.permissions)) {
            for (const officeKey of Object.keys(officeKeys)) {
                if (officeKeys[officeKey]) linear.push({ lobKey, officeKey, allow: true });
            }
        }
        const headerValue = { roles: this.roles, permissions: linear };
        return headerValue;
    }

    toSubjectRolesAndPermissions(): SubjectRolesAndPermissions {
        const lobOfficePermissions: LobOfficePermission[] = [];
        for (const [lobKey, officeKeys] of Object.entries(this.permissions)) {
            for (const officeKey of Object.keys(officeKeys)) {
                lobOfficePermissions.push({ lobKey, officeKey, allow: officeKeys[officeKey] });
            }
        }
        return { roles: this.roles, lobOfficePermissions };
    }

    static fromLobList(roles:  string[], lobList: AppPermissionsByLobRow[]): AppRoleAndPermissions {
        const appRolePermission = new AppRoleAndPermissions(roles);
        lobList.forEach(row => {
            const lobKey = row.lobKey;
            const officePermissions = {} as any;
            appRolePermission.permissions[lobKey] = officePermissions;
            Object.keys(row)
                .filter(key => key !== 'lobKey' && key !== 'lobName')
                .forEach(officeKey => officePermissions[officeKey] = !!row[officeKey]);
        });
        return appRolePermission;
    }

    static fromHeaderValue(headerValue: AppRolePermissionHeader): AppRoleAndPermissions {
        const appRolePermission = new AppRoleAndPermissions(headerValue.roles);
        headerValue.permissions.forEach(({ lobKey, officeKey }) => {
            appRolePermission.setPermission(lobKey, officeKey, true);
        });
        return appRolePermission;
    }

    static fromUserProfileData(userProfile: UserProfileData): AppRoleAndPermissions {
        const appRolePermission = new AppRoleAndPermissions(Array.from(userProfile.roles) ?? []);
        userProfile.permissions.forEach(({ lobKey, officeKey, allow }) => {
            appRolePermission.setPermission(lobKey, officeKey, allow);
        });
        return appRolePermission;
    }
}
